import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';

export default function Notifications({ open, onClose, notifications = [], role }) {
  const navigate = useNavigate();
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [notificationError, setNotificationError] = useState(null);

  console.log('Notifications: Received notifications:', notifications, 'Role:', role);

  useEffect(() => {
    const filterNotifications = async () => {
      if (!open) {
        console.log('Notifications: Dialog closed, skipping filter');
        setFilteredNotifications([]);
        return;
      }

      if (!notifications.length) {
        console.log('Notifications: No notifications received');
        setFilteredNotifications([]);
        return;
      }

      let filtered = [];
      const roleLower = role.toLowerCase();
      console.log('Notifications: Filtering for role:', roleLower);

      if (roleLower === 'kitchen_staff') {
        filtered = notifications.filter(n => n.recipientType === 'kitchen' || !n.recipientType);
        console.log('Notifications: Kitchen notifications:', filtered);
      } else if (roleLower === 'servers') {
        filtered = notifications.filter(n => n.recipientType === 'servers' || !n.recipientType);
        console.log('Notifications: Server notifications:', filtered);
      } else {
        console.warn('Notifications: Unknown role:', roleLower);
      }
      setFilteredNotifications(filtered);
    };

    filterNotifications();
  }, [open, notifications, role]);

  const handleNotificationClick = async (notificationId, notification) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      console.log(`Notifications: Deleted notification ${notificationId}`);

      let route;
      let state = { orderId: notification.orderId };

      
      if (notificationId.startsWith('kitchen')) {
        route = '/kitchen_staff/view-order';
      } else if (notificationId.startsWith('server') && notification.type !== 'cash_payment_request') {
        route = '/servers/orders';
      } else {
        
        route = '/servers/payment';
        state = {
          orderId: notification.orderId,
          tableId: notification.tableId,
          totalAmount: notification.totalAmount,
        };
      }

      console.log(`Notifications: Navigating to ${route} with state:`, state);
      navigate(route, { state });

      onClose();
    } catch (error) {
      console.error('Notifications: Error deleting notification:', error);
      setNotificationError('Failed to delete notification: ' + error.message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="notifications-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="notifications-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Notifications
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {notificationError && (
          <Typography variant="body1" color="error" sx={{ textAlign: 'center', py: 2 }}>
            {notificationError}
          </Typography>
        )}
        {filteredNotifications.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No new notifications
          </Typography>
        ) : (
          <List sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification.id, notification)}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    backgroundColor: notification.type === 'cash_payment_request' ? '#e3f2fd' : notification.recipientType === 'servers' ? '#fff9c4' : 'inherit',
                    '&:hover': {
                      backgroundColor: notification.type === 'cash_payment_request' ? '#bbdefb' : notification.recipientType === 'servers' ? '#ffecb3' : '#f5f5f5',
                    },
                  }}
                >
                  <ListItemText
                    primary={notification.message || 'No message provided'}
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Order ID: {notification.orderId || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Table ID: {notification.tableId || 'N/A'}
                        </Typography>
                        {notification.totalAmount && (
                          <Typography variant="body2" color="text.secondary">
                            Amount: ₹{notification.totalAmount.toFixed(2)}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Time: {notification.createdAt?.toDate?.().toLocaleString() || 'N/A'}
                        </Typography>
                      </>
                    }
                    primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}